import style from './workout-themed-section.module.scss'
import { ForwardArrowLink } from '../forward-arrow-link/forward-arrow-link'
import { useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { WorkoutPopup } from 'src/component/programmes/programme-workout-popup'
import { Skeleton } from 'src/component/ui/skeleton'
import { WorkoutCard } from 'src/component/ui/workout-card'
import { WorkoutCardSkeleton } from 'src/component/ui/workout-card/workout-card-skeleton'
import { useSessionContext } from 'src/context/session-context'
import { Workout } from 'src/dao/core-dao'
import { useWorkouts } from 'src/hook/use-workouts'
import { cn } from 'src/lib/utils'
import { WorkoutFilter } from 'src/service/core-service'

/**
 * `featured` renders the GH-754 mosaic for the 5-card sections: one large card spanning two rows on
 * the chosen side, with the other four in a 2×2 grid beside it (Dumbbell → left, Bodyweight →
 * right). Unset renders the standard 3-up row (Barbell, Express).
 */
export const WorkoutThemedSection = ({
  theme,
  label = '',
  seeAllLink = '',
  workoutFilter,
  areSectionsLoading = false,
  previewCount = 3,
  featured,
}: {
  theme: 'dark' | 'light'
  seeAllLink?: string
  label?: string
  workoutFilter?: WorkoutFilter
  areSectionsLoading?: boolean
  previewCount?: number
  featured?: 'left' | 'right'
}): React.ReactElement | null => {
  const { data: workouts = [], isLoading } = useWorkouts(
    areSectionsLoading ? undefined : { ...(workoutFilter || {}), page: 1, pageSize: previewCount },
    { enabled: !areSectionsLoading },
  )
  const { completedWorkoutIds } = useSessionContext()
  const [selectedWorkout, setSelectedWorkout] = useState<Workout | undefined>(undefined)

  const isActuallyLoading = areSectionsLoading || isLoading

  // Don't render the section if there are no items (after loading completes)
  if (!isActuallyLoading && (workouts?.length ?? 0) === 0) {
    return null
  }

  const renderCard = (workout: Workout, className?: string): React.ReactElement => (
    <WorkoutCard.List
      workout={workout}
      isCompleted={completedWorkoutIds.has(workout.id)}
      onAction={() => setSelectedWorkout(workout)}
      className={className}
    />
  )

  const renderCards = (): React.ReactElement => {
    if (featured) {
      // Mosaic from `lg` up: the featured (large) card spans both rows on one side and the other four
      // fill the remaining 2×2; below `lg` it collapses to a single column. The featured cell is
      // always rendered FIRST so (a) it sits on top in the single-column stack and (b) grid
      // auto-placement flows the four small cards around its explicitly-placed cell — otherwise the
      // right-side variant (Bodyweight) overlaps a small card. Left/right is chosen via col-start.
      const featuredPlacement = cn('lg:row-span-2 lg:row-start-1', featured === 'left' ? 'lg:col-start-1' : 'lg:col-start-3')

      if (isActuallyLoading) {
        return (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className={featuredPlacement}>
              <WorkoutCardSkeleton aspectRatio="aspect-[16/9] lg:aspect-auto" className="lg:h-full" />
            </div>
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index}>
                <WorkoutCardSkeleton />
              </div>
            ))}
          </div>
        )
      }

      const [featuredWorkout, ...rest] = workouts
      const gridWorkouts = rest.slice(0, 4)
      return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {featuredWorkout && (
            <div className={featuredPlacement}>{renderCard(featuredWorkout, 'rounded-md lg:aspect-auto lg:h-full')}</div>
          )}
          {gridWorkouts.map((workout) => (
            <div key={workout.id}>{renderCard(workout, 'rounded-md')}</div>
          ))}
        </div>
      )
    }

    return (
      <Row>
        {isActuallyLoading &&
          Array.from({ length: previewCount }).map((_, index) => (
            <Col key={index} sm={6} lg={4} className="mb-6">
              <WorkoutCardSkeleton />
            </Col>
          ))}
        {!isActuallyLoading &&
          workouts.map((workout) => (
            <Col key={workout.id} sm={6} lg={4} className="mb-6">
              {renderCard(workout)}
            </Col>
          ))}
      </Row>
    )
  }

  return (
    <Container className={`${style.workoutStyleSectionContainer} ${style[theme]}`}>
      <Row noGutters={true} className="mb-10">
        <Col className="d-flex justify-content-between align-items-center">
          {areSectionsLoading ? (
            <Skeleton className="h-12 w-64 bg-primary-gray" />
          ) : (
            <h2 className="font-evogria text-primary text-4xl uppercase">{label}</h2>
          )}
          <div className={style.seeAll}>
            {!areSectionsLoading && <ForwardArrowLink text="See All" link={seeAllLink} theme={theme} />}
          </div>
        </Col>
      </Row>
      {renderCards()}
      <WorkoutPopup
        setOnClose={() => setSelectedWorkout(undefined)}
        selectedWorkout={selectedWorkout}
        startWorkoutLink={selectedWorkout ? `/workouts/${selectedWorkout?.id}` : undefined}
      />
    </Container>
  )
}
